import { useState, useEffect } from "react";
import { fetchOpenAIText } from "../utils/openaiClient";

const getWordDetails = async (word) => {
  try {
    const prompt = `For the English word "${word}":
1. Determine its CEFR level (A1, A2, B1, or B2)
2. Provide its Russian translation (just the word, no explanation)
3. Create a simple example sentence in English
Respond ONLY with valid JSON in this format: {"level": "X", "translation": "Y", "example": "Z"}`;
    const content = await fetchOpenAIText({
      prompt,
      temperature: 0.2,
      maxTokens: 150,
    });
    const contentStr = content || '';

    let level = 'B1';
    let translation = 'Translation not available';
    let example = null;

    try {
      // Попробуем найти JSON
      const match = contentStr.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        level = parsed.level || level;
        translation = parsed.translation || translation;
        example = parsed.example || example;
      } else {
        // Если JSON нет, попробуем вытащить вручную
        const levelMatch = contentStr.match(/level["']?\s*[:=]\s*["']?([AB]\d)["']?/i);
        if (levelMatch) level = levelMatch[1];
        const translationMatch = contentStr.match(/translation["']?\s*[:=]\s*["']?([^,"'\n]+)["']?/i);
        if (translationMatch) translation = translationMatch[1];
        const exampleMatch = contentStr.match(/example["']?\s*[:=]\s*["']?([^,"'\n]+)["']?/i);
        if (exampleMatch) example = exampleMatch[1];
      }
    } catch (e) {
      // fallback
    }

    return { level, translation, example };
  } catch (error) {
    console.error('Error getting word details:', error);
    return {
      level: 'B1',
      translation: 'Translation not available',
      example: null
    };
  }
};

export function useGroupDictionary(groupId) {
  // eslint-disable-next-line no-unused-vars
  const dummyDictionaryEntries = [
    { id: 1, word: "Cat", level: "A1", translation: "Кошка", example: "I have a cat.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
    { id: 2, word: "Animal", level: "A1", translation: "Животное", example: "Both children are real animal lovers.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
    { id: 3, word: "Elephant", level: "A2", translation: "Слон", example: "In the past eight years, the elephant population in Africa has been halved.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
  ];
  const [dictionarySearchTerm, setDictionarySearchTerm] = useState('');

  const [isAddWordModalOpen, setIsAddWordModalOpen] = useState(false);
  const [newWord, setNewWord] = useState('');
  const [dictionaryEntries, setDictionaryEntries] = useState(() => {
    const saved = localStorage.getItem(`dictionaryEntries_${groupId}`);
    return saved ? JSON.parse(saved) : [];
  });

  const [editingWordId, setEditingWordId] = useState(null);
  const [editingWordData, setEditingWordData] = useState({
    word: '',
    level: '',
    translation: '',
    example: ''
  });

  useEffect(() => {
    localStorage.setItem(`dictionaryEntries_${groupId}`, JSON.stringify(dictionaryEntries));
  }, [dictionaryEntries, groupId]);

  const handleAddWordClick = () => {
    setIsAddWordModalOpen(true);
  };

  const handleAddWordSubmit = async (e) => {
    e.preventDefault();
    if (newWord.trim()) {
      setIsAddWordModalOpen(false);

      // Получаем детали слова через OpenAI
      const wordDetails = await getWordDetails(newWord.trim());

      const newWordEntry = {
        id: Date.now(),
        word: newWord.trim(),
        level: wordDetails.level || 'B1',
        translation: wordDetails.translation || 'Перевод недоступен',
        example: wordDetails.example || null,
        lesson: {
          title: 'Current Lesson',
          thumbnail: null
        },

      };

      setDictionaryEntries(prev => [...prev, newWordEntry]);
      setNewWord('');
    }
  };

  const handleDeleteWord = (wordId) => {
    setDictionaryEntries(prev => prev.filter(entry => entry.id !== wordId));
  };

  // Функция для воспроизведения произношения слова
  const playWordAudio = (word) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = 'en-US';
      utterance.rate = 0.8; // Немного медленнее для лучшего понимания
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
    } else {
      console.log('Speech synthesis not supported');
    }
  };

  return {
    dictionarySearchTerm, setDictionarySearchTerm,
    isAddWordModalOpen, setIsAddWordModalOpen,
    newWord, setNewWord,
    dictionaryEntries, setDictionaryEntries,
    editingWordId, setEditingWordId,
    editingWordData, setEditingWordData,
    handleAddWordClick, handleAddWordSubmit, handleDeleteWord, playWordAudio,
  };
}
